<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { create as maskCreate } from 'maska';
import { useAuthStore } from '~/stores/auth';
import { isNilOrEmpty, onlyAlphanumeric } from '~/utils';
import confirmarAjuizamentoFormGqlQuery from '~/graphql/confirmarAjuizamentoForm.graphql';
import acaoSelectGqlQuery from '~/graphql/acaoSelect.graphql';
import usuarioSelectGqlQuery from '~/graphql/usuarioSelect.graphql';
import agenciaEmailSelectGqlQuery from '~/graphql/agenciaEmailSelect.graphql';
import { useDrawerStore } from '~/stores/drawer';

const props = defineProps({
  compromisso: {
    default: null,
    type: Object,
  },
});

const emit = defineEmits(['close', 'loaded', 'success', 'error']);

const drawerStore = useDrawerStore();
const authStore = useAuthStore();
const agenciaEmailsRef = ref<any>(null);

let mask: any = null;

const numeroCnjInputRef = ref<any>(null);
const peticaoInicialRef = ref<any>(null);

async function preSubmit(model: any)
{
  console.log(model);

  await peticaoInicialRef.value.upload();

  model.numeroCnj.value = onlyAlphanumeric(model.numeroCnj.value);

  return true;
}

function abrirNovoAgenciaEmailDrawer(agenciaCodigo: string)
{
  drawerStore.push2({
    componentName: 'NovoAgenciaEmailDrawer',
    params: {
      agenciaCodigo,
    },
    onClose: async () =>
    {
      await agenciaEmailsRef.value.reloadAsync();
    },
  });
}

onMounted(() =>
{
  emit('loaded');
});

onUnmounted(() =>
{
  if (mask)
  {
    mask.destroy();
  }
});

watch(numeroCnjInputRef, (val) =>
{
  if (val)
  {
    mask = maskCreate('.numero-cnj', {
      mask: '#######-##.####.#.##.####',
    });
  }
});
</script>

<template>
  <div>
    <GqlView
      :query="confirmarAjuizamentoFormGqlQuery"
      :variables="{
        pastaCodigo: props.compromisso.fluxo.pastaCodigo,
      }"
    >
      <template #success="{ data }: {data: any}">
        <CompromissoJobCommandForm
          command="ConfirmarAjuizamentoCommand"
          :show-upload-files="true"
          :submit-button-disabled="!isNilOrEmpty(compromisso)
            && compromisso.responsavel.codigo !== authStore.codigoUsuario"
          :initial-model="{
            compromissoCodigos: props.compromisso ? [props.compromisso.codigo] : null,
            ...data.model,
          }"
          :pre-submit="preSubmit"
          @close="() => emit('close')"
        >
          <template #form="{ model }">
            <a-form-item
              label="Data de ajuizamento"
              name="dataAjuizamento"
              :rules="{
                message: 'Data de ajuizamento',
                required: true,
              }"
            >
              <a-date-picker
                v-model:value="model.dataAjuizamento"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DDTHH:mm:ss.SSS"
                placeholder="Selecione uma data"
                style="width: 100%"
                :allow-clear="true"
              />
            </a-form-item>

            <a-form-item
              label="Número CNJ"
              name="numeroCnj"
              :rules="{ required: true, message: 'Número CNJ é obrigatório' }"
            >
              <a-input
                ref="numeroCnjInputRef"
                v-model:value="model.numeroCnj"
                :allow-clear="true"
                class="numero-cnj"
              />
            </a-form-item>

            <a-form-item
              label="Vara"
              name="vara"
              :rules="{ required: true, message: 'Vara é obrigatória' }"
            >
              <a-input
                v-model:value="model.vara"
                :allow-clear="true"
              />
            </a-form-item>

            <a-form-item
              label="Ação"
              name="acao"
              :rules="{
                message: 'Ação é obrigatória',
                required: true,
                trigger: 'change',
                type: 'object',
              }"
            >
              <GqlSelect
                v-model:value="model.acao"
                :query="acaoSelectGqlQuery"
                placeholder="Selecione a ação"
                :allow-clear="true"
              />
            </a-form-item>

            <a-form-item
              label="Responsável processual"
              name="responsavelProcessual"
              :rules="{
                message: 'Responsável processual é obrigatório',
                required: true,
                trigger: 'change',
                type: 'object',
              }"
            >
              <GqlSelect
                v-model:value="model.responsavelProcessual"
                :query="usuarioSelectGqlQuery"
                placeholder="Selecione o responsável"
                :allow-clear="true"
              />
            </a-form-item>

            <a-form-item
              label="Responsável negocial"
              name="responsavelNegocial"
              :rules="{
                message: 'Responsável negocial é obrigatório',
                required: true,
                trigger: 'change',
                type: 'object',
              }"
            >
              <GqlSelect
                v-model:value="model.responsavelNegocial"
                :query="usuarioSelectGqlQuery"
                placeholder="Selecione o responsável"
                :allow-clear="true"
              />
            </a-form-item>

            <a-form-item
              label="Emails da agência que receberão o email de ajuizamento"
              name="agenciaEmails"
              :rules="{
                message: 'Email da agência é obrigatório',
                required: true,
                trigger: 'change',
              }"
            >
              <a-row :gutter="8">
                <a-col flex="auto">
                  <GqlSelect
                    ref="agenciaEmailsRef"
                    v-model:value="model.agenciaEmails"
                    :query="agenciaEmailSelectGqlQuery"
                    :allow-clear="true"
                    :variables="{
                      where: {
                        agenciaCodigo: {
                          in: !isNilOrEmpty(data.model.agenciaCodigo)
                            ? [data.model.agenciaCodigo]
                            : [],
                        },
                      },
                    }"
                    :disabled="isNilOrEmpty(data.model.agenciaCodigo)"
                    placeholder="Seleciona um ou mais emails da agência"
                    mode="multiple"
                  />
                </a-col>
                <a-col flex="70px">
                  <a-button
                    style="width: 70px"
                    :disabled="isNilOrEmpty(data.model.agenciaCodigo)"
                    @click="() => abrirNovoAgenciaEmailDrawer(data.model.agenciaCodigo)"
                  >
                    Novo
                  </a-button>
                </a-col>
              </a-row>
            </a-form-item>

            <a-form-item
              label="Petição inicial"
              name="peticaoInicial"
              :rules="{
                message: 'Petição inicial é obrigatória',
                required: true,
                trigger: 'change',
                type: 'object',
              }"
            >
              <UploadFile
                ref="peticaoInicialRef"
                v-model:file="model.peticaoInicial"
              />
            </a-form-item>
          </template>
        </Compromissojobcommandform>
      </template>
    </GqlView>
  </div>
</template>
